const VAT_RATE = 0.12,
  PRIVILEGE_RATE = 0.2;

// PWD, Senior Citizen and Indigenous (see fakeDb Privileges)
export const VAT_EXEMPT_PRIVILEGES = [1, 2, 3];

export const isVatExemptPrivilege = (privilege = 0) =>
  VAT_EXEMPT_PRIVILEGES.includes(Number(privilege || 0));

export const round2 = (value = 0) =>
  Math.round((Number(value || 0) + Number.EPSILON) * 100) / 100;

/**
 * PWD, Senior Citizen and Indigenous discount, following the service invoice:
 * Total Sales - VAT = Amount Net of VAT, Less 20% of the net, Add VAT back.
 * Lines keep centavos, only the final amount due is rounded to whole pesos
 * by the caller.
 * e.g. 200 - VAT 21.43 = 178.57 - 20% (35.71) + VAT 21.43 = 164.29 -> due 164
 */
const vatExemptDiscount = (gross = 0) => {
  const price = Number(gross || 0),
    vatExempt = round2(price / (1 + VAT_RATE)), // amount net of VAT
    vat = round2(price - vatExempt),
    lessDiscount = round2(vatExempt * PRIVILEGE_RATE),
    up = round2(price - lessDiscount);

  return { vat, vatExempt, lessDiscount, up, discount: lessDiscount };
};

/**
 * Invoice VAT lines for a manual (manager) discount on a VAT-inclusive total:
 * Total - VAT = Net of VAT, Less Discount, Add VAT back.
 * Saved on the deal so the claim stub prints the same breakdown.
 */
export const invoiceVat = (total = 0, discount = 0) => {
  const price = Number(total || 0),
    less = round2(discount);
  // no discount, or a free (100%) sale: nothing is billed, so no VAT lines
  if (!(less > 0) || less >= price)
    return { vat: 0, vatExempt: 0, lessDiscount: 0 };

  const vatExempt = round2(price / (1 + VAT_RATE));
  return { vat: round2(price - vatExempt), vatExempt, lessDiscount: less };
};

/**
 * Printout lines of a saved deal: the fixed price items (no discount) apart
 * from the discountable ones, which carry the VAT lines.
 * Total = Fixed Price + Discountable; Fixed Price + Discounted = Amount Due.
 */
export const vatLines = ({
  amount = 0,
  discount = 0,
  vat = 0,
  vatExempt = 0,
  lessDiscount = 0,
  cart = [],
} = {}) => {
  const total = round2(Number(amount || 0) + Number(discount || 0)),
    items = Array.isArray(cart) ? cart : [];

  // the sold items tell which ones got no discount (fixed price);
  // service carts carry up, clinic carts carry the line amount
  const fromCart = items.length
    ? round2(
        items
          .filter((item) => !(Number(item?.discount || 0) > 0))
          .reduce(
            (sum, item) =>
              sum + Number(item?.amount ?? item?.up ?? 0) + Number(item?.discount || 0),
            0,
          ),
      )
    : round2(total - Number(vat || 0) - Number(vatExempt || 0));
  // centavos left by older per-item rounding are not a fixed price
  const fixed = fromCart >= 1 && fromCart < total ? fromCart : 0,
    discountable = round2(total - fixed),
    net = round2(discountable / (1 + VAT_RATE));

  return {
    total,
    fixed,
    discountable,
    // VAT lines of the discountable subtotal only
    vat: fixed ? round2(discountable - net) : round2(vat),
    vatExempt: fixed ? net : round2(vatExempt),
    discounted: round2(discountable - Number(lessDiscount || 0)),
  };
};

/**
 * Original (VAT-inclusive) total of a deal before any discount.
 * Taken from the sold items, since amount + discount can drift after edits.
 */
export const getDealGross = (deal = {}) => {
  const { cart = [], amount = 0, discount = 0 } = deal || {};
  if (Array.isArray(cart) && cart.length)
    return round2(
      cart.reduce(
        (sum, item) =>
          sum + Number(item?.up || 0) + Number(item?.discount || 0),
        0,
      ),
    );

  return round2(Number(amount || 0) + Number(discount || 0));
};

export default vatExemptDiscount;
